// Anchored mini-profile + quick-actions popover — el.call('openPersonPopover',
// email, anchorEl). Mirrors the openProfileModal / openPersonDetailModal /
// openTicketDetailModal convention (this file's siblings): stash the target
// on root state, then trigger the surface that reads it back reactively.
//
// Unlike those three this is NOT a @symbo.ls/windows modal layer — it's a
// small anchored dropdown, the SAME @symbo.ls/dropdown layer AppOrgSwitcher
// and AppNavbar's More menu use (DropdownRoot mounted once at the shell root
// in app.js). ESC + outside-click dismiss and auto-flip positioning are
// already owned by that shared layer — nothing to wire here for those.
//
// `content` on the dropdown layer is a static descriptor (see
// @symbo.ls/dropdown openDropdown — it has no per-open payload channel), so
// `root._personPopoverEmail` is the data channel the content component
// (PersonPopoverCard, components/PersonPopover.js) reads.

const POPOVER_ID = 'personPopover'

export function openPersonPopover(email, anchorEl, displayName) {
  if (!email) return
  const root = this?.state?.root || this?.state
  if (!root) return
  // §3.7 Directory: an address outside the member roster resolves against
  // Party emails — fired here, at the mutation site, never from a popover
  // lifecycle hook; the card reads root._partyByEmail back reactively.
  // Fire-and-forget: the popover renders its email fallback meanwhile.
  const known = this.call('findPerson', email)
  if (!known) this.call('resolvePartyByEmail', email)
  // `selectedPerson` no longer carries geometry — the dropdown layer owns
  // placement since SharedPersonCard was retired. It stays as the ASSISTANT'S
  // awareness channel: the detectOpenRecord ladder in functions/pageContext.js
  // and AiContextBar's subscription list both key off it, so removing its last
  // writer left the assistant blind to an open person. `onClose` below clears
  // it, which is what made this key unusable before — nothing cleared it.
  root.update({
    _personPopoverEmail: email,
    _personPopoverName: displayName || null,
    selectedPerson: known || { email, name: displayName || null }
  })
  this.call('openDropdown', {
    anchorEl: anchorEl || this,
    id: POPOVER_ID,
    // The dock sits at the viewport bottom — prefer opening upward. The
    // plugin auto-flips to bottom-start when there isn't enough headroom
    // (e.g. this same popover reused from a top-of-page avatar later).
    placement: 'top-start',
    // Every dismissal lands here — Escape, outside click, an action button,
    // a scroll — so the awareness channel closes with the surface it
    // describes. `_personPopoverEmail` deliberately survives: the card still
    // reads it while the layer tears down, and reopening the same person
    // rewrites the same value.
    //
    // DEFERRED, and it must stay deferred. Written synchronously, this root
    // update lands WHILE the dropdown layer tears its panel down, and the
    // panel's own `Actions` children factory re-runs against a half-detached
    // element: `[smbls] children: factory threw at
    // …DropdownRoot.PanelHost.Panel.content.Actions: el.call is not a
    // function` (live QA, 2026-09-20, first open/close cycle only — a race,
    // so a green suite will not catch it). Same cure and same reason as the
    // search launcher's own onClose (functions/searchApps.js).
    onClose: () => queueMicrotask(() => root.update({ selectedPerson: null })),
    content: { extends: 'PersonPopoverCard' }
  })
}

// Close helper for parity with the open/close pairs of its siblings —
// PersonPopoverCard's own action buttons close via the bare
// `el.call('closeDropdown')` (no id needed: it always targets whatever
// layer is currently open, and a button inside this popover can only ever
// be clicked while THIS popover is the open layer).
export function closePersonPopover() {
  this.call('closeDropdown', POPOVER_ID)
}
